import qs from 'qs'
import dayjs from 'dayjs'
import { pickBy } from 'lodash'
import { HALF_HOUR_TIME_OPTIONS } from '@/utils/half-hour-time-options'
import { hasReservationTaskPlugin } from '@/libs/plugins'
import { TPlugin } from '@/models/plugin'
import {
  RESERVATION_TASK_HANDLING_STATUSES,
  RESERVATION_TASK_RESERVATION_STATUSES,
  RESERVATION_TASK_TAB_TYPE,
  ReservationTaskFilter,
  ReservationTaskHandlingStatus,
  ReservationTaskListParams,
  ReservationTaskQuery,
  ReservationTaskReservationStatus,
  ReservationTaskSort,
  ReservationTaskSortKey,
  ReservationTaskStaff,
} from '@/models/reservation/reservation-task'

export const DEFAULT_RESERVATION_TASK_SORT: ReservationTaskSort = {
  key: ReservationTaskSortKey.ReservationUpdatedAt,
  order: 'desc',
}

export const EMPTY_RESERVATION_TASK_FILTER: ReservationTaskFilter = {
  reservationStatuses: [],
  handlingStatuses: [],
  staffIds: [],
  reservationUpdatedAtFrom: '',
  reservationUpdatedAtTo: '',
  checkinDateFrom: '',
  checkinDateTo: '',
}

/** タスク管理タブの初期条件（タブの見出しを押した直後・絞り込みのクリア）。完了したタスクは最初から見せない */
export const INITIAL_RESERVATION_TASK_QUERY: ReservationTaskQuery = {
  filter: {
    ...EMPTY_RESERVATION_TASK_FILTER,
    handlingStatuses: [ReservationTaskHandlingStatus.Unhandled, ReservationTaskHandlingStatus.InProgress],
  },
  sort: DEFAULT_RESERVATION_TASK_SORT,
  page: 1,
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const DATE_TIME_PATTERN = /^(\d{4}-\d{2}-\d{2})(?: (\d{2}:\d{2}))?$/

const reservationStatusValues: readonly number[] = RESERVATION_TASK_RESERVATION_STATUSES
const handlingStatusValues: readonly number[] = RESERVATION_TASK_HANDLING_STATUSES
const sortKeyValues: readonly string[] = Object.values(ReservationTaskSortKey)

export const isReservationTaskReservationStatus = (value: number): value is ReservationTaskReservationStatus =>
  reservationStatusValues.includes(value)

export const isReservationTaskHandlingStatus = (value: number): value is ReservationTaskHandlingStatus =>
  handlingStatusValues.includes(value)

const isSortKey = (value: unknown): value is ReservationTaskSortKey => typeof value === 'string' && sortKeyValues.includes(value)

const toArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value
  // qs は添字が多い・飛んでいる配列を { '0': 'a' } のオブジェクトで返すため、値だけを取り出す
  if (value !== null && typeof value === 'object') return Object.values(value as Record<string, unknown>)
  return value === undefined ? [] : [value]
}

const toPositiveIntegers = (value: unknown): number[] =>
  toArray(value)
    .map(item => Number(item))
    .filter(item => Number.isInteger(item) && item > 0)

/**
 * 書式だけでは 2026-02-30 のような実在しない日付が通り、絞り込みパネルの日付欄（moment）を開くと例外になる。
 * dayjs は実在しない日付を次の月へ繰り越して読むため、書き戻して元の文字列に戻るかで実在を確かめる
 */
const isExistingDate = (date: string): boolean => dayjs(date).format('YYYY-MM-DD') === date

const pickDate = (value: unknown): string => (typeof value === 'string' && DATE_PATTERN.test(value) && isExistingDate(value) ? value : '')

/** 時刻は時刻欄で選べる30分刻みだけを残す。画面からはそれ以外の時刻を作れないため、手で書き換えた値とみなす */
const pickDateTime = (value: unknown): string => {
  const match = typeof value === 'string' ? DATE_TIME_PATTERN.exec(value) : null
  if (!match) return ''
  const [text, date, time] = match
  return isExistingDate(date) && (time === undefined || HALF_HOUR_TIME_OPTIONS.includes(time)) ? text : ''
}

const isFilled = (value: ReservationTaskFilter[keyof ReservationTaskFilter]): boolean =>
  Array.isArray(value) ? value.length > 0 : value !== ''

// 型引数を省くと lodash の Dictionary 版の型が選ばれ、絞り込み条件の型検査が効かなくなるため明示する
const pickFilledFilter = (filter: ReservationTaskFilter): Partial<ReservationTaskFilter> => pickBy<ReservationTaskFilter>(filter, isFilled)

/** 絞り込み条件が1つでも指定されているか。フィルターボタンの見た目の切り替えに使う */
export const hasReservationTaskFilter = (filter: ReservationTaskFilter): boolean => Object.values(filter).some(isFilled)

/** 並び替えの見出しや URL から届いた並び順を、API が受け付ける並び順にする。知らない項目は既定に戻す */
export const toReservationTaskSort = (sort: { key?: unknown; order?: unknown } | undefined): ReservationTaskSort => {
  if (!sort || !isSortKey(sort.key)) {
    return DEFAULT_RESERVATION_TASK_SORT
  }
  return { key: sort.key, order: sort.order === 'asc' ? 'asc' : 'desc' }
}

/**
 * URL のクエリから一覧の条件を読む。手で書き換えられた値や古い形式の値は捨てて既定値に戻す。
 * URL がタブの指定だけ（タブの見出しを押した直後）なら、復元する条件が無いので null を返す
 */
export const parseReservationTaskQuery = (search: string): ReservationTaskQuery | null => {
  const parsed = qs.parse(search, { ignoreQueryPrefix: true })
  if (Object.keys(parsed).every(key => key === 'tabType')) {
    return null
  }
  const page = Number(parsed.page)
  const sortKey = parsed.sortKey
  return {
    filter: {
      reservationStatuses: toPositiveIntegers(parsed.reservationStatuses).filter(isReservationTaskReservationStatus),
      handlingStatuses: toPositiveIntegers(parsed.handlingStatuses).filter(isReservationTaskHandlingStatus),
      staffIds: toPositiveIntegers(parsed.staffIds),
      reservationUpdatedAtFrom: pickDateTime(parsed.reservationUpdatedAtFrom),
      reservationUpdatedAtTo: pickDateTime(parsed.reservationUpdatedAtTo),
      checkinDateFrom: pickDate(parsed.checkinDateFrom),
      checkinDateTo: pickDate(parsed.checkinDateTo),
    },
    sort: toReservationTaskSort(typeof sortKey === 'object' && !Array.isArray(sortKey) ? sortKey : undefined),
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  }
}

/**
 * 一覧の条件を URL のクエリにする。空の条件は書かず URL を短く保つ。
 * 宿泊管理画面（list.tsx）も同じ URL を読むため、並び順は他のタブと同じ { key, order } の形で書く
 */
export const stringifyReservationTaskQuery = (query: ReservationTaskQuery): string =>
  qs.stringify({ tabType: RESERVATION_TASK_TAB_TYPE, page: query.page, sortKey: query.sort, ...pickFilledFilter(query.filter) })

/**
 * 予約更新日時を、バックエンドが受け付ける唯一の形 'Y-m-d H:i:s' にする（設計書 4.2）。
 * 画面の時刻欄は分までなので、開始は :00 秒、終了は指定した分の1分間を含めるよう :59 秒を付ける。
 * 日付だけのときは、開始はその日の 00:00:00、終了はその日の 23:59:59 にして、その日をまるごと含める
 */
const toApiUpdatedAtFrom = (value: string): string => (value.includes(' ') ? `${value}:00` : `${value} 00:00:00`)
const toApiUpdatedAtTo = (value: string): string => (value.includes(' ') ? `${value}:59` : `${value} 23:59:59`)

/** 一覧 API のパラメーターにする。空の条件は送らない */
export const toReservationTaskListParams = (query: ReservationTaskQuery): ReservationTaskListParams => {
  const filter = pickFilledFilter(query.filter)
  return {
    ...filter,
    ...(filter.reservationUpdatedAtFrom ? { reservationUpdatedAtFrom: toApiUpdatedAtFrom(filter.reservationUpdatedAtFrom) } : {}),
    ...(filter.reservationUpdatedAtTo ? { reservationUpdatedAtTo: toApiUpdatedAtTo(filter.reservationUpdatedAtTo) } : {}),
    sortKey: query.sort.key,
    sortOrder: query.sort.order,
    page: query.page,
  }
}

/** 日付欄と時刻欄の値を、API に送る1つの値（'YYYY-MM-DD' または 'YYYY-MM-DD HH:mm'）にする。日付が空なら時刻も捨てる */
export const joinDateTime = (date: string, time: string): string => {
  if (!date) return ''
  return time ? `${date} ${time}` : date
}

export const splitDateTime = (value: string): { date: string; time: string } => {
  const [date = '', time = ''] = value.split(' ')
  return { date, time }
}

/**
 * 予約更新日時の開始が終了より後になっていないか。どちらかが空なら範囲の制約は無いので true
 * 時刻の無い値は、開始はその日の 00:00、終了はその日の終わりとして比べる（バックエンドの解釈と同じ。設計書 4.2）
 */
export const isDateTimeRangeValid = (from: string, to: string): boolean => {
  if (!from || !to) return true
  const normalizedFrom = from.includes(' ') ? from : `${from} 00:00`
  const normalizedTo = to.includes(' ') ? to : `${to} 23:59`
  return normalizedFrom <= normalizedTo
}

/** AccountContext のスタッフ一覧（id が文字列のこともある）を、対応スタッフの選択肢にする。id か名前が無い行は選べないので除く */
export const toTaskStaffOptions = (rows: { id?: string | number; name?: string }[]): ReservationTaskStaff[] =>
  rows
    .map(row => ({ id: Number(row.id), name: row.name ?? '' }))
    .filter(staff => Number.isInteger(staff.id) && staff.id > 0 && staff.name !== '')

/** 割り当て済みのスタッフが選択肢に無い（スタッフ一覧の取得前・削除済みなど）ときも名前を出せるよう、末尾に加える */
export const withCurrentStaff = (options: ReservationTaskStaff[], current: ReservationTaskStaff | null): ReservationTaskStaff[] =>
  current && !options.some(option => option.id === current.id) ? [...options, current] : options

/**
 * URL でタスク管理タブが指定されているのに、その施設ではタブを表示しないか（宿泊管理画面がほかのタブへ切り替える判定に使う）。
 * plugins はアカウントの読み込み後に取得され、それまでは undefined。その間に false と判定すると、対象の施設まで切り替えてしまうため待つ
 */
export const isUnavailableReservationTaskTab = (tabType: string, plugins: TPlugin[] | undefined): boolean =>
  plugins !== undefined && tabType === RESERVATION_TASK_TAB_TYPE && !hasReservationTaskPlugin(plugins)
